/**
 * Data layer for `recurring-commission-saas-model`. Thin typed fetch wrappers over /api/v1/recurring-commission-saas-model.
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export interface RecurringCommissionSaasModelRow {
	id: string;
	tenant_id: string;
	created_at: number;
	updated_at: number;
	[key: string]: unknown;
}

export interface ListRecurringCommissionSaasModelResponse {
	count: number;
	items: RecurringCommissionSaasModelRow[];
}

export interface CreateRecurringCommissionSaasModelBody {
	[key: string]: unknown;
}

const BASE = "/api/v1/recurring-commission-saas-model";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListRecurringCommissionSaasModelResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListRecurringCommissionSaasModelResponse>(res);
}

export async function create(body: CreateRecurringCommissionSaasModelBody): Promise<RecurringCommissionSaasModelRow> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<RecurringCommissionSaasModelRow>(res);
}

export async function remove(id: string): Promise<{ removed: boolean }> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "DELETE",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<{ removed: boolean }>(res);
}

export const recurringCommissionSaasModelQueryKeys = {
	all: ["recurring-commission-saas-model"] as const,
	list: () => [...recurringCommissionSaasModelQueryKeys.all] as const,
	detail: (id: string) => [...recurringCommissionSaasModelQueryKeys.all, id] as const,
};
